import { memo, useEffect, useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../translations';
import { useSSRPageData } from '../lib/ssr-data';

type PageLayout = 'narrow' | 'article' | 'wide' | 'full';

const AnimatedBackground = memo(function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-purple-100/60 to-pink-100/50" />
      <div 
        className="absolute w-[600px] h-[600px] rounded-full blur-3xl animate-orb-1"
        style={{
          background: 'radial-gradient(circle, rgba(129, 140, 248, 0.6) 0%, transparent 70%)',
          top: '5%',
          left: '-15%',
        }}
      />
      <div 
        className="absolute w-[700px] h-[700px] rounded-full blur-3xl animate-orb-2"
        style={{
          background: 'radial-gradient(circle, rgba(167, 139, 250, 0.5) 0%, transparent 70%)',
          top: '35%',
          right: '-20%',
        }}
      />
      <div 
        className="absolute w-[500px] h-[500px] rounded-full blur-3xl animate-orb-3"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.5) 0%, transparent 70%)',
          bottom: '5%',
          left: '15%',
        }}
      />
      <div 
        className="absolute w-[450px] h-[450px] rounded-full blur-3xl animate-orb-4"
        style={{
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, transparent 70%)',
          top: '55%',
          left: '55%',
        }}
      />
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, #6366f1 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
    </div>
  );
});

interface PageData {
  id: number;
  slug: string;
  language: string;
  title: string;
  content?: string;
  contentHtml?: string;
  metaDescription?: string;
  ogImageUrl?: string;
  layout?: PageLayout;
}

const layoutStyles: Record<PageLayout, { container: string; article: string }> = {
  narrow: {
    container: 'max-w-3xl mx-auto px-6',
    article: 'bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-12',
  },
  article: {
    container: 'max-w-4xl mx-auto px-6',
    article: 'bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-12',
  },
  wide: {
    container: 'max-w-6xl mx-auto px-6',
    article: 'bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-16',
  },
  full: {
    container: 'w-full px-4 md:px-8',
    article: '',
  },
};

interface PageProps {
  slug: string;
}

const Page = memo<PageProps>(function Page({ slug }) {
  const { lang } = useLanguage();
  const ssrPageData = useSSRPageData();

  // Use pre-fetched SSR data if it matches the current slug & language
  const initialData: PageData | null =
    ssrPageData && ssrPageData.slug === slug && ssrPageData.language === lang
      ? (ssrPageData as PageData)
      : null;

  const [page, setPage] = useState<PageData | null>(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip fetch if we already have matching data with rendered HTML.
    // SSR hydration includes contentHtml so no fetch is needed on first load.
    // Client-side navigations fetch from the API (initialData won't match).
    if (page && page.slug === slug && page.language === lang) {
      setLoading(false);
      return;
    }

    const fetchPage = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/pages/${slug}/${lang}`);
        
        if (response.ok) {
          const data = await response.json();
          setPage(data.page);
          
          // Update document title
          if (data.page?.title) {
            document.title = `${data.page.title} | Ronnie Projects`;
          }
        } else if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError('Failed to load page');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const layout = (page?.layout || 'article') as PageLayout;
  const styles = layoutStyles[layout];

  if (loading) {
    return (
      <div className="min-h-screen relative pt-32 pb-20 overflow-hidden">
        <AnimatedBackground />
        <div className={`${styles.container} relative`} style={{ zIndex: 1 }}>
          <div className="animate-pulse bg-white/90 rounded-3xl p-8 md:p-12 shadow-xl backdrop-blur-sm">
            <div className="h-10 bg-slate-200/60 rounded w-1/3 mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-slate-200/60 rounded w-full" />
              <div className="h-4 bg-slate-200/60 rounded w-5/6" />
              <div className="h-4 bg-slate-200/60 rounded w-4/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !page) {
    const t = translations[lang].notFound;
    return (
      <div className="min-h-screen relative pt-32 pb-20 overflow-hidden">
        <AnimatedBackground />
        <div className="max-w-4xl mx-auto px-6 text-center relative" style={{ zIndex: 1 }}>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            {t.title}
          </h1>
          <p className="text-slate-600">
            {t.description}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-32 pb-20 overflow-hidden">
      <AnimatedBackground />
      <div className={`${styles.container} relative`} style={{ zIndex: 1 }}>
        {layout === 'full' ? (
          <>
            {page.ogImageUrl && (
              <img
                src={page.ogImageUrl}
                alt={page.title}
                className="w-full max-h-[480px] object-cover rounded-2xl mb-8 shadow-lg"
                loading="eager"
              />
            )}
            <div
              className="prose prose-slate max-w-none"
              suppressHydrationWarning
              dangerouslySetInnerHTML={{ __html: page.contentHtml || '' }}
            />
          </>
        ) : (
          <article className={`${styles.article} backdrop-blur-sm bg-white/90 shadow-xl`}>
            {page.ogImageUrl && (
              <img
                src={page.ogImageUrl}
                alt={page.title}
                className="w-full max-h-[480px] object-cover rounded-xl mb-8"
                loading="eager"
              />
            )}
            <div suppressHydrationWarning dangerouslySetInnerHTML={{ __html: page.contentHtml || '' }} />
          </article>
        )}
      </div>
    </div>
  );
});

export default Page;

